<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
import dayjs from 'dayjs';
import EGS from '@/util/egs_util.ts';

const props = defineProps({
	modelValue: {
		type: String,
		default: '' // "HH:mm"
	},
	disabled: {
		type: Boolean,
		default: false
	},
	placeholder: {
		type: String,
		default: 'Select time'
	},
	minuteStep: {
		type: Number,
		default: 1
	},
	appendToBody: {
		type: Boolean,
		default: true
	},
	inputId: {
		type: String,
		default: null
	},
	showTriggerIcon: {
		type: Boolean,
		default: true
	},
	triggerId: {
		type: String,
		default: null
	}
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);
const triggerRef = ref(null);
const panelRef = ref(null);
const position = ref({ top: 0, left: 0 });
const inputValue = ref('');

function parseModel(value) {
	if (!value) return null;
	const [hhRaw, mmRaw] = String(value).split(':');
	const hh = Number(hhRaw);
	const mm = Number(mmRaw);
	if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
	return { hh, mm };
}

function toModel(hh24, mm) {
	const h = String(hh24).padStart(2, '0');
	const m = String(mm).padStart(2, '0');
	return `${h}:${m}`;
}

function formatForDisplay(value) {
	if (!value) return '';
	const parsed = parseModel(value);
	if (!parsed) return String(value);
	const d = dayjs(`2000-01-01T${toModel(parsed.hh, parsed.mm)}:00`);
	if (!d.isValid()) return String(value);
	return d.format('hh:mm A');
}

function normalizeTypedValue(value) {
	const raw = String(value || '').trim();
	if (!raw) return '';

	try {
		const normalized = EGS.util.formatSaveTime(raw);
		return dayjs(`2000-01-01T${normalized}`).format('HH:mm');
	} catch {
		return null;
	}
}

const selected = ref(parseModel(props.modelValue) || { hh: 9, mm: 0 });
inputValue.value = formatForDisplay(props.modelValue);

watch(
	() => props.modelValue,
	(v) => {
		const parsed = parseModel(v);
		if (parsed) selected.value = parsed;
		inputValue.value = formatForDisplay(v);
	}
);

const hour12 = computed(() => {
	const h = selected.value.hh % 12;
	return h === 0 ? 12 : h;
});

const period = computed(() => (selected.value.hh >= 12 ? 'PM' : 'AM'));

const hours = computed(() => Array.from({ length: 12 }, (_, i) => i + 1));
const minutes = computed(() => {
	const step = Math.max(1, Math.min(30, Number(props.minuteStep) || 1));
	const out = [];
	for (let i = 0; i < 60; i += step) out.push(i);
	return out;
});

function updatePosition() {
	if (!triggerRef.value || !panelRef.value) return;
	const rect = triggerRef.value.getBoundingClientRect();
	const panelRect = panelRef.value.getBoundingClientRect();
	const padding = 8;

	let top = rect.bottom + 8;
	let left = rect.left;

	// flip up if needed
	if (top + panelRect.height > window.innerHeight - padding) {
		top = rect.top - panelRect.height - 8;
	}

	// clamp horizontally
	if (left + panelRect.width > window.innerWidth - padding) {
		left = window.innerWidth - panelRect.width - padding;
	}
	if (left < padding) left = padding;
	if (top < padding) top = padding;

	position.value = { top, left };
}

function open() {
	if (props.disabled) return;
	isOpen.value = true;
	nextTick(updatePosition);
}

function close() {
	isOpen.value = false;
}

function toggle() {
	if (isOpen.value) close();
	else open();
}

function commit() {
	const value = toModel(selected.value.hh, selected.value.mm);
	inputValue.value = formatForDisplay(value);
	emit('update:modelValue', value);
}

function setHour(h12) {
	const isPm = period.value === 'PM';
	let hh = h12 % 12;
	if (isPm) hh += 12;
	selected.value.hh = hh;
	commit();
}

function setMinute(mm) {
	selected.value.mm = mm;
	commit();
}

function setPeriod(p) {
	const h12 = hour12.value;
	let hh = h12 % 12;
	if (p === 'PM') hh += 12;
	selected.value.hh = hh;
	commit();
}

function onInput(e) {
	const value = e.target.value;
	inputValue.value = value;

	if (!value.trim()) {
		emit('update:modelValue', '');
	}
}

function commitTypedValue() {
	if (!inputValue.value.trim()) {
		emit('update:modelValue', '');
		return;
	}

	const normalized = normalizeTypedValue(inputValue.value);
	if (!normalized) return;

	const parsed = parseModel(normalized);
	if (parsed) selected.value = parsed;
	inputValue.value = formatForDisplay(normalized);
	emit('update:modelValue', normalized);
}

function onInputBlur() {
	commitTypedValue();
}

function onInputKeydown(e) {
	if (e.key === 'Enter') {
		e.preventDefault();
		commitTypedValue();
		close();
	}
}

function onDocMouseDown(e) {
	if (!isOpen.value) return;
	const t = e.target;
	if (triggerRef.value?.contains(t)) return;
	if (panelRef.value?.contains(t)) return;
	close();
}

function onKeyDown(e) {
	if (e.key === 'Escape' && isOpen.value) close();
}

function onScrollOrResize() {
	if (isOpen.value) updatePosition();
}

onMounted(() => {
	document.addEventListener('mousedown', onDocMouseDown);
	document.addEventListener('keydown', onKeyDown);
	window.addEventListener('scroll', onScrollOrResize, true);
	window.addEventListener('resize', onScrollOrResize);
});

onBeforeUnmount(() => {
	document.removeEventListener('mousedown', onDocMouseDown);
	document.removeEventListener('keydown', onKeyDown);
	window.removeEventListener('scroll', onScrollOrResize, true);
	window.removeEventListener('resize', onScrollOrResize);
});
</script>

<template>
	<div class="relative">
		<div ref="triggerRef" :class="{ 'opacity-60': disabled }">
			<div class="relative">
				<input
					:id="inputId || triggerId || undefined"
					:value="inputValue"
					type="text"
					:disabled="disabled"
					:placeholder="placeholder"
					class="w-full rounded-lg border border-surface bg-[var(--egs-content-background)] px-3 py-2.5 text-sm text-color transition-colors hover:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-[var(--egs-content-hover)] disabled:opacity-60"
					:class="showTriggerIcon ? 'pr-10' : ''"
					@focus="open"
					@input="onInput"
					@blur="onInputBlur"
					@keydown="onInputKeydown"
				/>
				<button
					v-if="showTriggerIcon"
					:id="triggerId || undefined"
					type="button"
					class="absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded-md p-1 text-color-secondary transition-colors hover:text-color"
					:disabled="disabled"
					@click="toggle"
				>
					<font-awesome-icon :icon="['far', 'clock']" class="shrink-0" />
				</button>
			</div>
		</div>

		<Teleport to="body" :disabled="!appendToBody">
			<div v-if="isOpen" ref="panelRef" class="fixed z-[10060] rounded-xl border border-surface bg-[var(--egs-content-background)] shadow-dropdown p-2.5 w-[260px]" :style="{ top: position.top + 'px', left: position.left + 'px' }">
				<div class="grid grid-cols-[1fr_1fr_0.8fr] gap-2.5">
					<div>
						<div class="text-[0.7rem] font-bold text-color-secondary uppercase tracking-wide mb-1.5">Hour</div>
						<div class="max-h-[220px] overflow-auto rounded-lg border border-surface bg-[var(--egs-content-background)]">
							<button
								v-for="h in hours"
								:key="h"
								type="button"
								class="w-full py-2 px-2.5 border-none bg-transparent text-center cursor-pointer text-color text-sm transition-colors hover:bg-[var(--egs-content-hover)]"
								:class="{ '!bg-primary/[0.12] !font-bold': h === hour12 }"
								@click="setHour(h)"
							>
								{{ String(h).padStart(2, '0') }}
							</button>
						</div>
					</div>

					<div>
						<div class="text-[0.7rem] font-bold text-color-secondary uppercase tracking-wide mb-1.5">Min</div>
						<div class="max-h-[220px] overflow-auto rounded-lg border border-surface bg-[var(--egs-content-background)]">
							<button
								v-for="m in minutes"
								:key="m"
								type="button"
								class="w-full py-2 px-2.5 border-none bg-transparent text-center cursor-pointer text-color text-sm transition-colors hover:bg-[var(--egs-content-hover)]"
								:class="{ '!bg-primary/[0.12] !font-bold': m === selected.mm }"
								@click="setMinute(m)"
							>
								{{ String(m).padStart(2, '0') }}
							</button>
						</div>
					</div>

					<div>
						<div class="text-[0.7rem] font-bold text-color-secondary uppercase tracking-wide mb-1.5">AM/PM</div>
						<div class="rounded-lg border border-surface bg-[var(--egs-content-background)]">
							<button
								type="button"
								class="w-full py-2 px-2.5 border-none bg-transparent text-center cursor-pointer text-color text-sm transition-colors hover:bg-[var(--egs-content-hover)]"
								:class="{ '!bg-primary/[0.12] !font-bold': period === 'AM' }"
								@click="setPeriod('AM')"
							>
								AM
							</button>
							<button
								type="button"
								class="w-full py-2 px-2.5 border-none bg-transparent text-center cursor-pointer text-color text-sm transition-colors hover:bg-[var(--egs-content-hover)]"
								:class="{ '!bg-primary/[0.12] !font-bold': period === 'PM' }"
								@click="setPeriod('PM')"
							>
								PM
							</button>
						</div>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>
